<script setup lang="ts">
import type { BlockTeamMember } from '#shared/types/schema';
import Headline from '~/components/base/Headline.vue';
import DirectusImage from '~/components/shared/DirectusImage.vue';
import Text from '~/components/base/Text.vue';

interface BlockTeamProps {
	data: {
		id?: string | number;
		headline?: string | null;
		members?: BlockTeamMember[] | (string | number)[];
	};
	background?: 'light' | 'dark' | null;
}

const props = defineProps<BlockTeamProps>();
const { setAttr } = useVisualEditing();

// Filtra e ordina i membri validi
const sortedMembers = computed(() => {
	if (!props.data?.members) return [];
	
	return props.data.members
		.filter((member): member is BlockTeamMember => {
			return typeof member === 'object' && member !== null && 'id' in member;
		})
		.sort((a, b) => {
			const sortA = a.sort ?? 0;
			const sortB = b.sort ?? 0;
			return sortA - sortB;
		});
});

const textColor = computed(() => {
	if (props.background === 'dark') {
		return 'text-white';
	}
	return 'text-black';
});

// Funzione helper per ottenere l'ID dell'immagine
const getImageId = (image: string | { id: string } | null | undefined): string | null => {
	if (!image) return null;
	if (typeof image === 'string') return image;
	if (typeof image === 'object' && 'id' in image) return image.id;
	return null;
};
</script>

<template>
	<section>
		<div v-if="data.headline" class="text-center mb-12">
			<Headline
				:headline="data.headline"
				text-color="text-black"
				:data-directus="
					data.id
						? setAttr({
								collection: 'block_team',
								item: String(data.id),
								fields: 'headline',
								mode: 'popover',
							})
						: undefined
				"
			/>
		</div>

		<div
			v-if="sortedMembers.length > 0"
			class="grid gap-8"
			:class="{
				'grid-cols-1': sortedMembers.length === 1,
				'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3': sortedMembers.length > 1,
			}"
			:data-directus="
				data.id
					? setAttr({
							collection: 'block_team',
							item: String(data.id),
							fields: ['members'],
							mode: 'modal',
						})
					: undefined
			"
		>
			<div
				v-for="member in sortedMembers"
				:key="member.id"
				class="flex flex-col items-start text-left bg-white p-6 rounded-lg aspect-[2/3]"
			>
				<!-- Immagine del membro -->
				<div
					v-if="getImageId(member.image)"
					class="relative w-full mb-6 overflow-hidden flex-shrink-0"
					style="aspect-ratio: 1 / 1;"
					:data-directus="
						data.id && member.id
							? setAttr({
									collection: 'block_team_members',
									item: member.id,
									fields: 'image',
									mode: 'modal',
								})
							: undefined
					"
				>
					<DirectusImage
						:uuid="getImageId(member.image)!"
						:alt="member.full_name || 'Team member'"
						class="w-full h-full object-cover"
					/>
				</div>
				
				<!-- Nome -->
				<h3
					v-if="member.full_name"
					class="text-xl font-heading font-semibold mb-2 text-black"
					:data-directus="
						data.id && member.id
							? setAttr({
									collection: 'block_team_members',
									item: member.id,
									fields: 'full_name',
									mode: 'popover',
								})
							: undefined
					"
				>
					{{ member.full_name }}
				</h3>
				
				<!-- Ruolo -->
				<p
					v-if="member.role"
					class="text-base font-medium mb-4 text-accent"
					:data-directus="
						data.id && member.id
							? setAttr({
									collection: 'block_team_members',
									item: member.id,
									fields: 'role',
									mode: 'popover',
								})
							: undefined
					"
				>
					{{ member.role }}
				</p>
				
				<!-- Descrizione -->
				<div
					v-if="member.description"
					class="text-sm leading-relaxed w-full"
					:data-directus="
						data.id && member.id
							? setAttr({
									collection: 'block_team_members',
									item: member.id,
									fields: 'description',
									mode: 'drawer',
								})
							: undefined
					"
				>
					<Text
						:content="member.description"
						text-color="text-black"
						size="sm"
						class="max-w-none w-full prose-p:text-black prose-headings:text-black prose-ul:text-black prose-ol:text-black prose-li:text-black prose-blockquote:text-black prose-p:text-sm prose-headings:text-sm prose-ul:text-sm prose-ol:text-sm prose-li:text-sm prose-blockquote:text-sm [&_p]:!mb-0 [&_p+_p]:!mt-2"
					/>
				</div>
			</div>
		</div>
	</section>
</template>

